import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Alert, Box, Button, CircularProgress, Typography } from '@shared/ui';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetTeamQuery } from '../../store/api/apiSlice';

export default function MembersByEmailRedirectPage() {
  const navigate = useNavigate();
  const { email: rawEmail } = useParams();
  const hasNavigatedRef = useRef(false);

  const email = (rawEmail ?? '').trim();
  const normalizedEmail = email.toLowerCase();

  const { data: teamData, isLoading, isFetching, error } = useGetTeamQuery();

  const matchedMember = useMemo(() => {
    if (!normalizedEmail || !teamData?.members?.length) {
      return null;
    }

    return (
      teamData.members.find(
        (member) => member.person.primary_email?.trim().toLowerCase() === normalizedEmail
      ) ?? null
    );
  }, [normalizedEmail, teamData?.members]);

  useEffect(() => {
    if (!matchedMember || hasNavigatedRef.current) {
      return;
    }

    hasNavigatedRef.current = true;
    navigate(`/members/${matchedMember.person.id}`, { replace: true });
  }, [matchedMember, navigate]);

  const errorMessage = useMemo(() => {
    if (!error) {
      return null;
    }
    const fetchError = error as FetchBaseQueryError;
    if (fetchError.status === 401) {
      return 'Please sign in to view members.';
    }
    if (fetchError.status === 403) {
      return 'You do not have permission to view members.';
    }
    return 'Could not load your members. Please try again.';
  }, [error]);

  if (!email) {
    return (
      <Box sx={{ px: 3, py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Missing email in link.
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/members', { replace: true })}>
          Go to members
        </Button>
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box sx={{ px: 3, py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
        <Button variant="outlined" onClick={() => navigate('/members', { replace: true })}>
          Go to members
        </Button>
      </Box>
    );
  }

  if (isLoading || isFetching) {
    return (
      <Box sx={{ px: 3, py: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Looking up {email}…</Typography>
      </Box>
    );
  }

  if (matchedMember) {
    return (
      <Box sx={{ px: 3, py: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography>Redirecting…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 4 }}>
      <Alert severity="warning" sx={{ mb: 2 }}>
        No related member found for {email}.
      </Alert>
      <Button variant="outlined" onClick={() => navigate('/members', { replace: true })}>
        Go to members
      </Button>
    </Box>
  );
}
